import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {Form, Formik} from 'formik';
import * as Yup from 'yup';
import * as Sentry from '@sentry/react';
import {Box, Button, Center, Container, Divider, Heading, HStack, Image, Text, VStack} from "@chakra-ui/react";
import {isEmpty} from "lodash";
import content from "../../constants/en";
import Error404 from '../../error/error404';
import Error500 from '../../error/error500';
import Loading from '../../loading';
import InteracLogo from '../../common/assets/interacIcon.svg';
import Clik2payLogo from '../../common/assets/Clik2payLogoColour.svg'
import {InputLabelField} from "../../components/input-label-field/input-label-field";
import BackgroundWrapper from "../../components/background-wrapper/background-wrapper";
import {INITIAL_PAYER_DETAILS_INPUT} from '../model/payer-details';
import {
  MAX_AMOUNT_MESSAGE,
  MAX_MESSAGE,
  MIN_AMOUNT_MESSAGE,
  MIN_MESSAGE,
  REGEXP_ACCOUNT_ID_MESSAGE,
  REGEXP_FULL_NAME_MESSAGE,
  REGEXP_INVOICE_NUMBER_MESSAGE,
  REGEXP_NAME_MESSAGE,
  REGEXP_PHONE_NUMBER_MESSAGE,
  REQUIRED_MESSAGE,
} from '../../constants/validation-messages';

const NAME_REGEX = /^[a-zA-Z0-9àâäèéêëîïôœùûüÿçÀÂÄÈÉÊËÎÏÔŒÙÛÜŸÇ '\-.]+$/;
const VALID_AMOUNT_REGEX = /^(\d+)(\.?)(\d){0,2}$/;
const VALID_EMAIL_REGEX = /^[\w!#$%&’*+/=?`{|}~^-]+(?:\.[\w!#$%&’*+/=?`{|}~^-]+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,6}$/;
const PHONE_NUMBER_REGEX = /^[2-9][0-8][0-9][2-9][0-9]{6}$/;
const ACCOUNT_ID_REGEX = /^[a-zA-Z0-9]+$/
const INVOICE_NUMBER_REGEX = /^[a-zA-Z0-9\-*()_#;&/]+$/
const STRING_WITH_SPACES_REGEX = /^(\S+ )+\S+$/
const DEFAULT_MINIMUM_AMOUNT = 0.01;
const DEFAULT_MAXIMUM_AMOUNT = 10000.00;

function getCustomerNameSchema(isCustomerFullNameRequired) {
  const schema = Yup.string()
    .max(100, MAX_MESSAGE)
    .min(3, MIN_MESSAGE)
    .required(REQUIRED_MESSAGE);
  if (isCustomerFullNameRequired) {
    return schema.matches(STRING_WITH_SPACES_REGEX, REGEXP_FULL_NAME_MESSAGE);
  } else {
    return schema.matches(NAME_REGEX, REGEXP_NAME_MESSAGE);
  }
}

function getAccountIdSchema(isAccountIdRequired) {
  const schema = Yup.string()
    .max(50, MAX_MESSAGE)
    .min(4, MIN_MESSAGE)
    .matches(ACCOUNT_ID_REGEX, REGEXP_ACCOUNT_ID_MESSAGE
    );
  if (isAccountIdRequired) {
    return schema.required(REQUIRED_MESSAGE)
  }
  return schema.notRequired()
}

function getInvoiceNumberSchema(isInvoiceNumberRequired) {
  const schema = Yup.string()
    .max(50, MAX_MESSAGE)
    .min(4, MIN_MESSAGE)
    .matches(INVOICE_NUMBER_REGEX, REGEXP_INVOICE_NUMBER_MESSAGE
    );
  if (isInvoiceNumberRequired) {
    return schema.required(REQUIRED_MESSAGE)
  }
  return schema.notRequired()
}

function getMobileNumberSchema(isMobileNumberRequired) {
  const schema = Yup.string()
    .matches(PHONE_NUMBER_REGEX, REGEXP_PHONE_NUMBER_MESSAGE);
  if (isMobileNumberRequired) {
    return schema.required(REQUIRED_MESSAGE)
  }
  return schema.notRequired();
}

export const getConfirmSmartLinkSchema = (minAmount, maxAmount, isAccountIdRequired = false, isInvoiceNumberRequired = false, isMobileNumberRequired = false, isCustomerFullNameRequired = false) =>
  Yup.object().shape({
    amount: Yup.number()
      .min(minAmount, MIN_AMOUNT_MESSAGE)
      .max(maxAmount, MAX_AMOUNT_MESSAGE)
      .required(REQUIRED_MESSAGE)
      .test('amount-decimals', content.confirmSmartCode.validation.decimal, amount => {
        return VALID_AMOUNT_REGEX.test(amount);
      }),
    customer_name: getCustomerNameSchema(isCustomerFullNameRequired),
    customer_email: Yup.string()
      .min(5, MIN_MESSAGE)
      .max(64, MAX_MESSAGE)
      .matches(VALID_EMAIL_REGEX, content.confirmSmartCode.validation.email)
      .required(REQUIRED_MESSAGE),
    account_id: getAccountIdSchema(isAccountIdRequired),
    invoice_number: getInvoiceNumberSchema(isInvoiceNumberRequired),
    customer_mobile: getMobileNumberSchema(isMobileNumberRequired)
  }
);

const getSmartLinkFulfillmentUrl = (smartLinkId) => `${window.config.FULFILLMENT_API}/smart-link/${smartLinkId}/fulfillment`

export const fulfillSmartLinkRequest = (values, reCaptchaValue, smartLinkId) => {
  fetch(getSmartLinkFulfillmentUrl(smartLinkId), {
    method: "POST",
      headers: {
          "Content-Type": "application/json",
          "x-recaptcha-token": reCaptchaValue,
      },
      body: JSON.stringify ({
        amount: values.amount,
        customerName: values.customer_name,
        customerEmail: values.customer_email,
        customerMobileNumber: !isEmpty(values.customer_mobile) ? values.customer_mobile: null,
        merchantAccountId: !isEmpty(values.account_id) ? values.account_id: null,
        merchantInvoiceNumber: !isEmpty(values.invoice_number) ? values.invoice_number: null,
      })
  }).then(function (resp) {
      if (!resp.ok) {
          window.location.replace("/error/" + resp.status);
      } else {
          resp.json().then(function (data) {
              window.location.replace(data.paymentLink);
          });
      }
  });
}

function ConfirmSmartCode({getRecaptchaToken}) {
  return (
    <BackgroundWrapper backgroundSize="contain">
      <Container maxW='549px' centerContent={true} paddingY={10} paddingX={4}>
        <Box
          width="full"
          boxShadow="0px 4px 32px 0px #00000030"
          borderRadius="md"
          paddingY="32px"
          paddingX="24px"
        >
          <DynamicSmartForm getRecaptchaToken={getRecaptchaToken}/>
        </Box>
      </Container>
    </BackgroundWrapper>
  )
}

function DynamicSmartForm({getRecaptchaToken}) {
  const {smartCode} = useParams()
  const [smartLinkDetails, setSmartLinkDetails] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState(null)

  const smartLinkEndpoint = `${window.config.FULFILLMENT_API}/smart-link/${smartCode}`

  const onSubmit = async (values, {setSubmitting}) => {
    setSubmitted(true)
    let token = await getRecaptchaToken()
    fulfillSmartLinkRequest(values, token, values.smartLinkId);
    setSubmitting(false);
  }

  const autoSubmit = async (smartLinkId) => {
    setSubmitted(true)
    let token = await getRecaptchaToken()
    fulfillSmartLinkRequest({}, token, smartLinkId);
  }

  useEffect(() => {
    getRecaptchaToken().then(recaptchaToken => {
      fetch(smartLinkEndpoint, {
        headers: {
          'Content-Type': 'application/json',
          'x-recaptcha-token': recaptchaToken,
        },
      }).then(res => res.json())
        .then(
          (result) => {
            setSmartLinkDetails(result);
            setLoading(false);
          },
          (error) => {
            setLoading(false);
            setError(error);
            Sentry.captureException(error);
          });
    })
  }, [smartLinkEndpoint, getRecaptchaToken]);

  if (loading || submitted) {
    return <Loading/>
  }

  if (!smartLinkDetails) {
    return <Error404/>
  }

  if (error) {
    return <Error500/>
  }

  const defaultCustomerName = smartLinkDetails.defaultCustomerName ? smartLinkDetails.defaultCustomerName : INITIAL_PAYER_DETAILS_INPUT.customer_name
  const defaultEmail = smartLinkDetails.defaultEmail ? smartLinkDetails.defaultEmail : INITIAL_PAYER_DETAILS_INPUT.customer_email
  const defaultAmount = smartLinkDetails.defaultAmount ? smartLinkDetails.defaultAmount : INITIAL_PAYER_DETAILS_INPUT.amount
  const defaultAccountId = smartLinkDetails.defaultAccountId ? smartLinkDetails.defaultAccountId : INITIAL_PAYER_DETAILS_INPUT.account_id;
  const defaultInvoiceNumber = smartLinkDetails.defaultInvoiceNumber ? smartLinkDetails.defaultInvoiceNumber : INITIAL_PAYER_DETAILS_INPUT.invoice_number;
  const defaultMobileNumber = smartLinkDetails.defaultMobileNumber ? smartLinkDetails.defaultMobileNumber : INITIAL_PAYER_DETAILS_INPUT.customer_mobile;
  const minimumAmount = smartLinkDetails.minimumAmount?.toFixed(2) ?? DEFAULT_MINIMUM_AMOUNT;
  const maximumAmount = smartLinkDetails.maximumAmount?.toFixed(2) ?? DEFAULT_MAXIMUM_AMOUNT;
  const isCustomerFullNameRequired = smartLinkDetails.customerFullNameRequired
  const isAccountIdRequired = smartLinkDetails.options?.merchantAccountId?.required ?? false
  const isInvoiceNumberRequired = smartLinkDetails.options?.invoiceNumber?.required ?? false
  const isMobileNumberRequired = smartLinkDetails.options?.mobileNumber?.required ?? false
  const isAccountIdVisible = smartLinkDetails.options?.merchantAccountId?.visible ?? false
  const isInvoiceNumberVisible = smartLinkDetails.options?.invoiceNumber?.visible ?? false
  const isMobileNumberVisible = smartLinkDetails.options?.mobileNumber?.visible ?? false
  const smartLinkId = smartLinkDetails.id

  const initialValues = {
    ...INITIAL_PAYER_DETAILS_INPUT,
    customer_name: defaultCustomerName,
    customer_email: defaultEmail,
    customer_mobile: defaultMobileNumber,
    amount: defaultAmount,
    smartLinkId: smartLinkId,
    account_id: defaultAccountId,
    invoice_number: defaultInvoiceNumber
  }

  if (defaultCustomerName !== INITIAL_PAYER_DETAILS_INPUT.customer_name
    && (defaultEmail !== INITIAL_PAYER_DETAILS_INPUT.customer_email || defaultMobileNumber !== INITIAL_PAYER_DETAILS_INPUT.customer_mobile)
    && defaultAmount !== INITIAL_PAYER_DETAILS_INPUT.amount) {
    autoSubmit(smartLinkId).then()
  }

  const copy = content.confirmSmartCode
  const validationSchema = getConfirmSmartLinkSchema(minimumAmount, maximumAmount, isAccountIdRequired, isInvoiceNumberRequired, isMobileNumberRequired, isCustomerFullNameRequired)

  const formContents = (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({isSubmitting}) => (
        <Form >
          <VStack
            alignItems="flex-start"
            mb="32px"
            gap="16px"
            data-testid="confirm-smart-code-form"
          >
            <InputLabelField
              name="customer_name"
              label={copy.form.nameLabel}
              maxLength={100}
              isReadOnly={defaultCustomerName !== INITIAL_PAYER_DETAILS_INPUT.customer_name}
            />
            <InputLabelField
              name="customer_email"
              label={copy.form.emailLabel}
              maxLength={64}
              isReadOnly={defaultEmail !== INITIAL_PAYER_DETAILS_INPUT.customer_email}
            />
            <InputLabelField
              name="amount"
              label={copy.form.amountLabel}
              isReadOnly={defaultAmount !== INITIAL_PAYER_DETAILS_INPUT.amount}
            />
            {isMobileNumberVisible &&
              <InputLabelField
              name="customer_mobile"
              label={copy.form.mobileLabel(isMobileNumberRequired)}
              maxLength={10}
              isReadOnly={defaultMobileNumber !== INITIAL_PAYER_DETAILS_INPUT.customer_mobile}
            />}
            {isAccountIdVisible &&
              <InputLabelField
              name="account_id"
              label={copy.form.accountIdLabel(isAccountIdRequired)}
              isReadOnly={defaultAccountId !== INITIAL_PAYER_DETAILS_INPUT.account_id}
            />
            }
            { isInvoiceNumberVisible &&
              <InputLabelField
                name="invoice_number"
                label={copy.form.invoiceNumberLabel(isInvoiceNumberRequired)}
                isReadOnly={defaultInvoiceNumber !== INITIAL_PAYER_DETAILS_INPUT.invoice_number}
              />
            }
            <Button
              isDisabled={isSubmitting}
              type="submit"
              textTransform="uppercase"
              colorScheme="primary"
              fontSize="14px"
              fontWeight="500"
              lineHeight="150%"
              width="100%"
              data-testid="button-continue"
              borderRadius={20}
              boxSizing="border-box"
              mt="8px"
            >
              {copy.form.submitButtonLabel}
            </Button>
          </VStack>
        </Form>
      )}
    </Formik>
  )

  return (
    <>
      <Center mb="32px">
        <Image
          minHeight="40px"
          maxHeight="48px"
          src={`${smartLinkDetails.billerLogo}`}
          alt={copy.merchantLogoAltText} />
      </Center>
      <Heading data-testid='confirm-smart-code-page-title' fontSize="20px" mb="16px" fontWeight="700" color="#1F2937">
        {copy.title}
      </Heading>
      <Text color="#27272A" fontSize="16px" mb="24px" mt={0}>
        {copy.subtitle}
      </Text>
      {formContents}
      <HStack justifyContent="center">
        <Center height="40px" gap="16px">
          <Image src={InteracLogo} alt={copy.interacLogoAltText} boxSize="40px"/>
          <Divider orientation='vertical' borderWidth="1.45px" borderColor="#737373"/>
          <Image src={Clik2payLogo} alt={copy.clik2payLogoAltText} height="30px" />
        </Center>
      </HStack>
      <Text align="center" fontSize="12px" mt="32px" mb={0}>
        {copy.footerMessage}
      </Text>
    </>
  )
}

export default ConfirmSmartCode
